/**
 * Dashboard Profile tab (view only): personal info, account settings, app preferences (BooksyAppExpo-style).
 * Edit and Change Password are rendered by Dashboard via ProfileEditForm / ProfilePasswordForm.
 */
import React, { useState, memo } from 'react';
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaCog,
  FaBell,
  FaShieldAlt,
  FaCalendar,
  FaKey,
  FaClock,
  FaLanguage,
  FaMoon,
  FaEdit,
  FaLock,
  FaChevronDown,
  FaChevronUp,
  FaExchangeAlt,
} from 'react-icons/fa';
import { useI18n } from '../../contexts/I18n';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, TabLoadingBuffer, Error, SuccessBanner } from './Card';
import AuthRequiredPanel, { isUnauthorizedError } from './AuthRequiredPanel';
import EmailVerificationBanner from './EmailVerificationBanner';

const ProfileTab = memo(function ProfileTab({
  profile,
  busyProfile,
  errProfile,
  msg,
  onEditProfile,
  onEditPassword,
  showEmailVerification,
  onEmailVerified,
  lang: langProp,
}) {
  const { t, lang: i18nLang } = useI18n();
  const lang = langProp || i18nLang;
  const { toggleLang } = useLanguage();
  const [personalInfoExpanded, setPersonalInfoExpanded] = useState(false);
  const [accountSettingsExpanded, setAccountSettingsExpanded] = useState(false);
  const [appPreferencesExpanded, setAppPreferencesExpanded] = useState(false);

  const rtlTextStyle = lang === 'ar' ? { textAlign: 'right', direction: 'rtl', width: '100%', display: 'block' } : {};

  const renderFieldWithIcon = (IconComponent, label, value) => (
    <div className="personal-info-field">
      <div className={`profile-info-header ${lang === 'ar' ? 'rtl' : ''}`}>
        {lang === 'ar' ? (
          <><span className={`profile-info-label ${lang === 'ar' ? 'rtl' : ''}`} style={rtlTextStyle}>{label}</span><div className="profile-info-icon-wrapper"><IconComponent size={18} color="var(--primary)" /></div></>
        ) : (
          <><div className="profile-info-icon-wrapper"><IconComponent size={18} color="var(--primary)" /></div><span className={`profile-info-label ${lang === 'ar' ? 'rtl' : ''}`} style={rtlTextStyle}>{label}</span></>
        )}
      </div>
      <div className={`profile-info-value ${lang === 'ar' ? 'rtl' : ''}`} style={rtlTextStyle}>{value}</div>
    </div>
  );

  if (busyProfile) return <TabLoadingBuffer label={t('dashLoading') || 'Loading...'} />;
  if (!profile) {
    if (isUnauthorizedError(errProfile)) {
      return (
        <Card extraClass="profile-card dashboard-auth-required-card">
          <AuthRequiredPanel hint={t('dashSessionExpiredProfileHint')} />
        </Card>
      );
    }
    return (
      <Card title={t('navProfile')}>
        <Error>{errProfile || t('unavailable')}</Error>
        {errProfile && (
          <p style={{ marginTop: '0.75rem', fontSize: '0.9rem', opacity: 0.85 }}>
            {lang === 'ar'
              ? 'اسحب للأسفل لإعادة المحاولة أو سجّل الخروج ثم الدخول مرة أخرى.'
              : 'Pull to refresh or log out and sign in again.'}
          </p>
        )}
      </Card>
    );
  }

  return (
    <>
      {showEmailVerification && (
        <div className="email-verify-banner-slot">
          <EmailVerificationBanner profile={profile} lang={lang} onVerified={onEmailVerified} />
        </div>
      )}
      {lang === 'ar' && (
        <style>{`
          .profile-info-item-card.rtl, html[dir="rtl"] .profile-info-item-card { direction: rtl !important; text-align: right !important; }
          .profile-info-item-card.rtl .personal-info-title, html[dir="rtl"] .profile-info-item-card .personal-info-title, .profile-info-item-card.rtl h3, html[dir="rtl"] .profile-info-item-card h3 { text-align: right !important; direction: rtl !important; }
          .profile-info-item-card.rtl .profile-info-label, html[dir="rtl"] .profile-info-item-card .profile-info-label, .profile-info-item-card.rtl span.profile-info-label, html[dir="rtl"] .profile-info-item-card span.profile-info-label { text-align: right !important; direction: rtl !important; }
          .profile-info-item-card.rtl .profile-info-value, html[dir="rtl"] .profile-info-item-card .profile-info-value, .profile-info-item-card.rtl div.profile-info-value, html[dir="rtl"] .profile-info-item-card div.profile-info-value { text-align: right !important; direction: rtl !important; }
        `}</style>
      )}
      <Card extraClass="profile-card profile--view">
        <div className={`profile-header ${lang === 'ar' ? 'rtl' : ''}`}>
          <div className={`profile-header-content ${lang === 'ar' ? 'rtl' : ''}`} />
        </div>
        <div className="profile-icon-container">
          <div className="profile-icon-wrapper"><FaUser size={100} color="var(--primary)" /></div>
        </div>
        {msg?.ok && /password/i.test(String(msg.ok)) && (
          <SuccessBanner>{msg.ok}</SuccessBanner>
        )}
        {errProfile && profile && (
          isUnauthorizedError(errProfile) ? (
            <AuthRequiredPanel hint={t('dashSessionExpiredProfileHint')} />
          ) : (
            <Error>{errProfile}</Error>
          )
        )}

        <div className={`profile-info-item-card ${lang === 'ar' ? 'rtl' : ''}`}>
          <button type="button" className={`personal-info-header ${lang === 'ar' ? 'rtl' : ''}`} onClick={() => setPersonalInfoExpanded(!personalInfoExpanded)} style={{ background: 'none', border: 'none', width: '100%', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 0 }}>
            <div className={`personal-info-title-row ${lang === 'ar' ? 'rtl' : ''}`}>
              {lang === 'ar' ? <><h3 className={`personal-info-title ${lang === 'ar' ? 'rtl' : ''}`} style={rtlTextStyle}>{t('personalInformation')}</h3><div className="profile-info-icon-wrapper"><FaUser size={20} color="var(--primary)" /></div></> : <><div className="profile-info-icon-wrapper"><FaUser size={20} color="var(--primary)" /></div><h3 className={`personal-info-title ${lang === 'ar' ? 'rtl' : ''}`} style={rtlTextStyle}>{t('personalInformation')}</h3></>}
            </div>
            {personalInfoExpanded ? <FaChevronUp size={20} color="var(--primary)" /> : <FaChevronDown size={20} color="var(--primary)" />}
          </button>
          {personalInfoExpanded && (
            <div className={`personal-info-content ${lang === 'ar' ? 'rtl' : ''}`}>
              {renderFieldWithIcon(FaUser, t('name'), profile.name || '—')}
              {renderFieldWithIcon(FaPhone, t('phone'), profile.phone || '—')}
              {renderFieldWithIcon(FaEnvelope, t('email'), profile.email || '—')}
            </div>
          )}
        </div>

        <div className={`profile-info-item-card ${lang === 'ar' ? 'rtl' : ''}`}>
          <button type="button" className={`personal-info-header ${lang === 'ar' ? 'rtl' : ''}`} onClick={() => setAccountSettingsExpanded(!accountSettingsExpanded)} style={{ background: 'none', border: 'none', width: '100%', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 0 }}>
            <div className={`personal-info-title-row ${lang === 'ar' ? 'rtl' : ''}`}>
              {lang === 'ar' ? <><h3 className={`personal-info-title ${lang === 'ar' ? 'rtl' : ''}`} style={rtlTextStyle}>{lang === 'ar' ? 'إعدادات الحساب' : 'Account Settings'}</h3><div className="profile-info-icon-wrapper"><FaCog size={20} color="var(--primary)" /></div></> : <><div className="profile-info-icon-wrapper"><FaCog size={20} color="var(--primary)" /></div><h3 className={`personal-info-title ${lang === 'ar' ? 'rtl' : ''}`} style={rtlTextStyle}>{lang === 'ar' ? 'إعدادات الحساب' : 'Account Settings'}</h3></>}
            </div>
            {accountSettingsExpanded ? <FaChevronUp size={20} color="var(--primary)" /> : <FaChevronDown size={20} color="var(--primary)" />}
          </button>
          {accountSettingsExpanded && (
            <div className={`personal-info-content ${lang === 'ar' ? 'rtl' : ''}`}>
              {renderFieldWithIcon(FaBell, lang === 'ar' ? 'إشعارات البريد الإلكتروني' : 'Email Notifications', lang === 'ar' ? 'مفعلة' : 'Enabled')}
              {renderFieldWithIcon(FaShieldAlt, lang === 'ar' ? 'أمان الحساب' : 'Account Security', lang === 'ar' ? 'آمن' : 'Secure')}
              {renderFieldWithIcon(FaCalendar, t('dashMemberSince') || 'Member Since', profile.createdAt ? new Date(profile.createdAt).toLocaleDateString('en-US') : '—')}
              {renderFieldWithIcon(FaKey, lang === 'ar' ? 'معرف المستخدم' : 'User ID', (profile._id || profile.id) ? String(profile._id || profile.id) : '—')}
              {renderFieldWithIcon(FaShieldAlt, lang === 'ar' ? 'حالة الحساب' : 'Account Status', lang === 'ar' ? 'نشط' : 'Active')}
              {renderFieldWithIcon(FaClock, lang === 'ar' ? 'آخر تسجيل دخول' : 'Last Login', profile.lastLogin ? new Date(profile.lastLogin).toLocaleDateString('en-US') : (lang === 'ar' ? 'اليوم' : 'Today'))}
            </div>
          )}
        </div>

        <div className={`profile-info-item-card ${lang === 'ar' ? 'rtl' : ''}`}>
          <button type="button" className={`personal-info-header ${lang === 'ar' ? 'rtl' : ''}`} onClick={() => setAppPreferencesExpanded(!appPreferencesExpanded)} style={{ background: 'none', border: 'none', width: '100%', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 0 }}>
            <div className={`personal-info-title-row ${lang === 'ar' ? 'rtl' : ''}`}>
              {lang === 'ar' ? <><h3 className={`personal-info-title ${lang === 'ar' ? 'rtl' : ''}`} style={rtlTextStyle}>{lang === 'ar' ? 'تفضيلات التطبيق' : 'App Preferences'}</h3><div className="profile-info-icon-wrapper"><FaCog size={20} color="var(--primary)" /></div></> : <><div className="profile-info-icon-wrapper"><FaCog size={20} color="var(--primary)" /></div><h3 className={`personal-info-title ${lang === 'ar' ? 'rtl' : ''}`} style={rtlTextStyle}>{lang === 'ar' ? 'تفضيلات التطبيق' : 'App Preferences'}</h3></>}
            </div>
            {appPreferencesExpanded ? <FaChevronUp size={20} color="var(--primary)" /> : <FaChevronDown size={20} color="var(--primary)" />}
          </button>
          {appPreferencesExpanded && (
            <div className={`personal-info-content ${lang === 'ar' ? 'rtl' : ''}`}>
              <button type="button" className={`profile-language-toggle ${lang === 'ar' ? 'rtl' : ''}`} onClick={toggleLang} aria-label={lang === 'ar' ? 'تغيير اللغة إلى الإنجليزية' : 'Change language to Arabic'}>
                <div className={`profile-info-header ${lang === 'ar' ? 'rtl' : ''}`}>
                  {lang === 'ar' ? <><span className={`profile-info-label ${lang === 'ar' ? 'rtl' : ''}`} style={rtlTextStyle}>{lang === 'ar' ? 'اللغة' : 'Language'}</span><div className="profile-info-icon-wrapper"><FaLanguage size={18} color="var(--primary)" /></div></> : <><div className="profile-info-icon-wrapper"><FaLanguage size={18} color="var(--primary)" /></div><span className={`profile-info-label ${lang === 'ar' ? 'rtl' : ''}`} style={rtlTextStyle}>{lang === 'ar' ? 'اللغة' : 'Language'}</span></>}
                </div>
                <div className={`profile-language-options ${lang === 'ar' ? 'rtl' : ''}`}>
                  <span className={`profile-language-option ${lang === 'en' ? 'active' : ''}`}>English</span>
                  <FaExchangeAlt size={18} color="var(--primary)" className="profile-language-swap-icon" aria-hidden="true" />
                  <span className={`profile-language-option ${lang === 'ar' ? 'active' : ''}`}>العربية</span>
                </div>
              </button>
              {renderFieldWithIcon(FaMoon, lang === 'ar' ? 'الوضع الليلي' : 'Dark Mode', lang === 'ar' ? 'فاتح' : 'Light')}
            </div>
          )}
        </div>

        <div className={`profile-actions-container ${lang === 'ar' ? 'rtl' : ''}`}>
          <div className={`profile-actions-row ${lang === 'ar' ? 'rtl' : ''}`}>
            <button type="button" className="profile-action-button profile-action-button-half" onClick={onEditProfile}>
              <FaEdit size={18} color="var(--white)" style={lang === 'ar' ? { marginLeft: '8px', marginRight: '0' } : { marginRight: '8px', marginLeft: '0' }} />
              {t('dashEditProfile') || 'Edit Profile'}
            </button>
            <button type="button" className="profile-action-button profile-action-button-secondary profile-action-button-half" onClick={onEditPassword}>
              <FaLock size={18} color="var(--primary)" style={lang === 'ar' ? { marginLeft: '8px', marginRight: '0' } : { marginRight: '8px', marginLeft: '0' }} />
              {t('dashChangePassword') || 'Change Password'}
            </button>
          </div>
        </div>
      </Card>
    </>
  );
});

export default ProfileTab;
